import { createContext, useContext, useState, useEffect, useMemo } from 'react'
import { usePromo } from './PromoContext'
import { detectBrowserLanguage } from '../utils/language'

const ClaimFormContext = createContext(null)
const STORAGE_KEY = 'claimform_state'
const STATE_VERSION = 2

const initialFlight = () => ({
  departure: '',
  destination: '',
  departureData: null,
  destinationData: null,
  date: '',
  flightNumber: '',
  departureTime: '',
  arrivalTime: '',
  airlineCode: '',
  airlineName: '',
})

const initialState = {
  _v: STATE_VERSION,
  currentFlightIndex: 0,
  lastReachedStep: '/step1',
  language: detectBrowserLanguage(),
  promo: null,
  partnerLogo: null,
  partnerWebsite: null,
  routeDeparture: '',
  routeDestination: '',
  routeDepartureData: null,
  routeDestinationData: null,
  flights: [],
  problem: {
    type: '',
    delayDuration: '',
    informDays: '',
    otherProblem: '',
  },
  passengers: 2,
  contact: {
    email: '',
    infoConsent: true,
    newsletter: false,
  },
  tariff: 'basic',
  commissionCrmId: null,
  passengerDetails: [],
  compensation: null,
  foundFlights: [],
  selectedFlight: null,
  flightSearchLoading: false,
  routeCheckResults: null,
  routeCheckNoClaim: false,
  routeCheckLoading: false,
  claimLinkNotFound: false,
  leadId: null,
  pendingLeadUpdate: false,
  caseUuid: null,
  caseReference: null,
  appParams: null,
  contractor: null,
  signatures: [],
  validation: {
    haveAdditionalCosts: null,
    additionalCostsAmount: '',
    receivedCompensation: null,
    refundedAmount: '',
    otherServiceProvider: null,
    pfGold: false,
    pfGoldBookingNumber: '',
    hasPromocode: false,
    promocode: '',
  },
}

const ROUTE_FIELDS = new Set(['routeDeparture', 'routeDestination', 'routeDepartureData', 'routeDestinationData'])

const loadState = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (!saved) return initialState
    const parsed = JSON.parse(saved)
    if (parsed._v !== STATE_VERSION) return initialState
    const merged = { ...initialState, ...parsed }
    const validation = { ...initialState.validation, ...parsed.validation }
    if (merged.promo && !validation.hasPromocode && !validation.promocode) {
      validation.hasPromocode = true
      validation.promocode = merged.promo
    }
    return { ...merged, validation }
  } catch {
    return initialState
  }
}

export function ClaimFormProvider({ children }) {
  const { promo, partnerLogo, partnerWebsite, contractor } = usePromo()
  const [data, setData] = useState(loadState)

  // Persist contractor without logo/website to avoid duplicating base64 in localStorage
  const contractorToPersist = useMemo(() => contractor
    ? { ...contractor, logoBase64: undefined, website: undefined }
    : null, [contractor])

  useEffect(() => {
    try {
      // exclude signatures (large base64) from localStorage to avoid quota errors
      const { signatures: _sigs, claimLinkLoading: _cll, ...persistable } = data
      localStorage.setItem(STORAGE_KEY, JSON.stringify(persistable))
    } catch {
      // ignore storage errors (e.g. private mode)
    }
  }, [data])

  // Sync promo, partner info and contractor from PromoContext to ClaimFormContext
  useEffect(() => {
    setData((prev) => {
      const updates = {}
      if (promo !== prev.promo) {
        updates.promo = promo
        if (promo && !prev.validation?.promocode) {
          updates.validation = { ...prev.validation, hasPromocode: true, promocode: promo }
        }
      }
      if (partnerLogo !== prev.partnerLogo) updates.partnerLogo = partnerLogo
      if (partnerWebsite !== prev.partnerWebsite) updates.partnerWebsite = partnerWebsite
      if (contractorToPersist !== prev.contractor) updates.contractor = contractorToPersist
      if (Object.keys(updates).length === 0) return prev
      return { ...prev, ...updates }
    })
  }, [promo, partnerLogo, partnerWebsite, contractorToPersist])

  const updateField = (path, value) => {
    setData((prev) => {
      const next = { ...prev, [path]: value }
      if (ROUTE_FIELDS.has(path) && prev.flights.length > 1) {
        next.routeCheckResults = null
        next.routeCheckNoClaim = false
        // Route changed: collapse connecting flights into a single flight
        // and keep the date already entered, but clear the selected flight.
        const first = prev.flights[0] || {}
        next.flights = [{
          ...first,
          flightNumber: '',
          departureTime: '',
          arrivalTime: '',
          airlineCode: '',
          airlineName: '',
          departure: next.routeDeparture || first.departure || '',
          destination: next.routeDestination || prev.routeDestination || '',
          departureData: next.routeDepartureData || first.departureData || null,
          destinationData: next.routeDestinationData || prev.routeDestinationData || null,
        }]
      } else if (ROUTE_FIELDS.has(path) && prev.flights.length === 1) {
        // Single flight: keep it in sync with the route fields.
        next.routeCheckResults = null
        next.routeCheckNoClaim = false
        const first = prev.flights[0] || {}
        const update = { ...first, flightNumber: '', departureTime: '', arrivalTime: '', airlineCode: '', airlineName: '' }
        if (path === 'routeDeparture') update.departure = value
        if (path === 'routeDestination') update.destination = value
        if (path === 'routeDepartureData') update.departureData = value
        if (path === 'routeDestinationData') update.destinationData = value
        next.flights = [update]
      }
      return next
    })
  }

  const updateFlight = (index, fields) => {
    setData((prev) => {
      const flights = [...prev.flights]
      flights[index] = { ...flights[index], ...fields }
      return { ...prev, flights }
    })
  }

  const addFlight = () => {
    setData((prev) => ({
      ...prev,
      flights: [...prev.flights, initialFlight()],
    }))
  }

  const removeFlight = (index) => {
    setData((prev) => {
      const flights = prev.flights.filter((_, i) => i !== index)
      return {
        ...prev,
        flights,
        currentFlightIndex: Math.min(prev.currentFlightIndex, flights.length - 1),
      }
    })
  }

  const reset = () => setData((prev) => ({ ...initialState, language: prev.language }))

  const value = {
    data,
    setData,
    updateField,
    updateFlight,
    addFlight,
    removeFlight,
    reset,
  }

  return <ClaimFormContext.Provider value={value}>{children}</ClaimFormContext.Provider>
}

export function useClaimForm() {
  const context = useContext(ClaimFormContext)
  if (!context) {
    throw new Error('useClaimForm must be used within ClaimFormProvider')
  }
  return context
}
